import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error | no-token
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("no-token");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((r) => {
        setStatus("success");
        setMessage(r.data?.message || "Email confirmed.");
        refresh();
      })
      .catch((e) => {
        setStatus("error");
        setMessage(formatApiError(e.response?.data?.detail) || "This link is invalid or has expired.");
      });
  }, [token, refresh]);

  return (
    <div data-testid="verify-page" className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="50 States Sober" className="w-16 h-16 rounded-full object-cover ring-1 ring-[var(--line)]" />
        </div>

        {status === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[var(--forest)] mb-4 animate-spin" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Confirming your email…</h1>
          </>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--forest)] mb-4" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Email confirmed</h1>
            <p className="text-[var(--ink-soft)] mb-8">{message} Welcome to the community.</p>
            <Link to="/" data-testid="verify-continue"
              className="inline-block bg-[var(--terracotta)] text-[var(--ink)] px-6 py-2.5 rounded-full text-sm hover:bg-[var(--terracotta-dark)]">
              Explore the site
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Something went wrong</h1>
            <p className="text-[var(--ink-soft)] mb-8">{message}</p>
            <Link to="/" data-testid="verify-home"
              className="inline-block border border-[var(--ink)] px-6 py-2.5 rounded-full text-sm hover:bg-[var(--ink)] hover:text-[var(--bg)]">
              Back to home
            </Link>
          </div>
        )}

        {status === "no-token" && (
          <div data-testid="verify-no-token">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Missing verification link</h1>
            <p className="text-[var(--ink-soft)] mb-8">This page needs a token in the URL to work. Please use the link from your welcome email.</p>
            <Link to="/" className="inline-block border border-[var(--ink)] px-6 py-2.5 rounded-full text-sm hover:bg-[var(--ink)] hover:text-[var(--bg)]">
              Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
